import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateRebarBeam, MODULE_REGISTRY } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

// =======================================================================
// RebarBeamModulePanel — the ONE Rebar Beam engineering calculation
// module, implementing the FINAL LOCKED Beam Contract (Longitudinal
// Groups + Stirrup Zones, architecturally distinct from Slab's Layer
// model). Reusable from BOTH BOQ Engineering Modules and Quantification
// Resource Quantity workflows via the `onSave(payload)` prop — this
// component itself never decides the persistence mechanism.
// =======================================================================

const BAR_DIAMETERS = [8, 10, 12, 16, 20, 25, 32, 40]

export default function RebarBeamModulePanel({ scopeId, boqItemId, linkedItems, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:rebar_beam:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, {
    selectedBeamIds: [],
    beamDims: {},
    covers: { top: '', bottom: '', side: '' },
    longitudinalGroups: [],
    stirrupZones: [],
    lapPct: '',
    wastePct: '',
  })

  const initial = superseding
    ? {
        selectedBeamIds: superseding.geometry_snapshot?.items?.map((i) => i.takeoff_item_id) || [],
        beamDims: superseding.engineering_inputs?.beamDims || {},
        covers: superseding.engineering_inputs?.covers || { top: '', bottom: '', side: '' },
        longitudinalGroups: superseding.engineering_inputs?.longitudinalGroups || [],
        stirrupZones: superseding.engineering_inputs?.stirrupZones || [],
        lapPct: superseding.engineering_inputs?.lapPct ?? '',
        wastePct: superseding.engineering_inputs?.wastePct ?? '',
      }
    : draft

  const [selectedBeamIds, setSelectedBeamIdsRaw] = useState(new Set(initial.selectedBeamIds))
  const [resolvedBeams, setResolvedBeams] = useState({})
  const [beamDims, setBeamDimsRaw] = useState(initial.beamDims)
  const [covers, setCoversRaw] = useState(initial.covers)
  const [longitudinalGroups, setLongitudinalGroupsRaw] = useState(initial.longitudinalGroups)
  const [stirrupZones, setStirrupZonesRaw] = useState(initial.stirrupZones)
  const [lapPct, setLapPctRaw] = useState(initial.lapPct)
  const [wastePct, setWastePctRaw] = useState(initial.wastePct)

  const setSelectedBeamIds = (updater) => {
    setSelectedBeamIdsRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      if (!superseding) setDraft((d) => ({ ...d, selectedBeamIds: [...next] }))
      return next
    })
  }
  const setBeamDims = (updater) => {
    setBeamDimsRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      if (!superseding) setDraft((d) => ({ ...d, beamDims: next }))
      return next
    })
  }
  const setCovers = (updater) => {
    setCoversRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      if (!superseding) setDraft((d) => ({ ...d, covers: next }))
      return next
    })
  }
  const setLongitudinalGroups = (updater) => {
    setLongitudinalGroupsRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      if (!superseding) setDraft((d) => ({ ...d, longitudinalGroups: next }))
      return next
    })
  }
  const setStirrupZones = (updater) => {
    setStirrupZonesRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      if (!superseding) setDraft((d) => ({ ...d, stirrupZones: next }))
      return next
    })
  }
  const setLapPct = (v) => { setLapPctRaw(v); if (!superseding) setDraft((d) => ({ ...d, lapPct: v })) }
  const setWastePct = (v) => { setWastePctRaw(v); if (!superseding) setDraft((d) => ({ ...d, wastePct: v })) }
  const [selectedBasis, setSelectedBasis] = useState('procurement')

  useEffect(() => {
    const idsToResolve = [...selectedBeamIds].filter((id) => !resolvedBeams[id])
    if (idsToResolve.length === 0) return
    ;(async () => {
      for (const id of idsToResolve) {
        const item = linkedItems.find((r) => r.id === id)
        const { data, error } = await supabase.from('takeoff_item_aggregated_quantity').select('aggregated_quantity').eq('takeoff_item_id', id).maybeSingle()
        if (error) { onError(error.message); continue }
        setResolvedBeams((prev) => ({ ...prev, [id]: { label: item?.description || id, length: data?.aggregated_quantity ?? 0 } }))
      }
    })()
  }, [selectedBeamIds, linkedItems, resolvedBeams, onError])

  function toggleBeam(id) {
    setSelectedBeamIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function updateBeamDim(id, patch) { setBeamDims((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } })) }

  function addLongitudinalGroup() {
    setLongitudinalGroups((prev) => [...prev, { id: `LG${Date.now()}`, label: '', position: 'bottom', diameter: '', numberOfBars: '', hookAllowance: '', developmentLengthOverride: '', explicitBarLength: '' }])
  }
  function updateLongitudinalGroup(id, patch) { setLongitudinalGroups((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g))) }
  function removeLongitudinalGroup(id) { setLongitudinalGroups((prev) => prev.filter((g) => g.id !== id)) }

  function addStirrupZone() {
    setStirrupZones((prev) => [...prev, { id: `SZ${Date.now()}`, label: '', startOffset: '', endOffset: '', diameter: '', spacing: '', legs: 2, numberOfHooksPerStirrup: 2, hookLengthPerHook: '' }])
  }
  function updateStirrupZone(id, patch) { setStirrupZones((prev) => prev.map((z) => (z.id === id ? { ...z, ...patch } : z))) }
  function removeStirrupZone(id) { setStirrupZones((prev) => prev.filter((z) => z.id !== id)) }

  const beamsForCalc = useMemo(() => {
    return [...selectedBeamIds]
      .map((id) => {
        const resolved = resolvedBeams[id]
        const dims = beamDims[id] || {}
        if (!resolved) return null
        return {
          id, label: resolved.label, length: resolved.length,
          width: dims.width === '' || dims.width == null ? NaN : Number(dims.width),
          overallDepth: dims.overallDepth === '' || dims.overallDepth == null ? NaN : Number(dims.overallDepth),
          topCover: covers.top === '' ? NaN : Number(covers.top),
          bottomCover: covers.bottom === '' ? NaN : Number(covers.bottom),
          sideCover: covers.side === '' ? NaN : Number(covers.side),
        }
      })
      .filter(Boolean)
  }, [selectedBeamIds, resolvedBeams, beamDims, covers])

  const longitudinalForCalc = useMemo(() => longitudinalGroups.map((g) => ({
    ...g,
    diameter: g.diameter === '' ? NaN : Number(g.diameter),
    numberOfBars: g.numberOfBars === '' ? NaN : Number(g.numberOfBars),
    hookAllowance: g.hookAllowance === '' ? 0 : Number(g.hookAllowance),
    developmentLengthOverride: g.developmentLengthOverride === '' ? 0 : Number(g.developmentLengthOverride),
    explicitBarLength: g.explicitBarLength === '' ? undefined : Number(g.explicitBarLength),
  })), [longitudinalGroups])

  const stirrupsForCalc = useMemo(() => stirrupZones.map((z) => ({
    ...z,
    startOffset: z.startOffset === '' ? NaN : Number(z.startOffset),
    // "Use Beam Length" substitutes the EXACT underlying beam.length here
    // (not the rounded value shown in the UI) — the estimator never has
    // to type the fully-precise calibrated length themselves, while the
    // strict exact-match zone-coverage validation remains fully intact.
    endOffset: z.useBeamLengthAsEnd ? (beamsForCalc[0]?.length ?? NaN) : (z.endOffset === '' ? NaN : Number(z.endOffset)),
    diameter: z.diameter === '' ? NaN : Number(z.diameter),
    spacing: z.spacing === '' ? NaN : Number(z.spacing),
    legs: Number(z.legs),
    numberOfHooksPerStirrup: Number(z.numberOfHooksPerStirrup),
    hookLengthPerHook: z.hookLengthPerHook === '' ? NaN : Number(z.hookLengthPerHook),
  })), [stirrupZones, beamsForCalc])

  // Beam calc runs per-beam independently, but zones/groups are shared
  // across all selected beams for simplicity — each beam still gets its
  // own full breakdown because dimensions differ per beam.
  const result = useMemo(() => {
    try {
      // Run once per beam (different beams may have different lengths,
      // so zone coverage validation must be checked per beam).
      const combined = { beamBreakdown: [], steps: null, quantityBases: null }
      let theoreticalSum = 0, lapSum = 0, wasteSum = 0, procurementSum = 0
      for (const beam of beamsForCalc) {
        const single = calculateRebarBeam([beam], longitudinalForCalc, stirrupsForCalc, {
          lapPct: lapPct === '' ? NaN : Number(lapPct), wastePct: wastePct === '' ? NaN : Number(wastePct),
        })
        combined.beamBreakdown.push(...single.beamBreakdown)
        theoreticalSum += single.quantityBases.theoretical.valueKg
        lapSum += single.steps[1].value
        procurementSum += single.quantityBases.procurement.valueKg
      }
      const theoreticalPlusLapSum = theoreticalSum + lapSum
      wasteSum = procurementSum - theoreticalPlusLapSum
      combined.steps = [
        { label: 'Theoretical Steel Weight', value: theoreticalSum, unit: 'kg' },
        { label: `Lap/Splice Allowance (${lapPct}%, simplified estimating allowance)`, value: lapSum, unit: 'kg' },
        { label: 'Theoretical + Lap', value: theoreticalPlusLapSum, unit: 'kg' },
        { label: `Waste Allowance (${wastePct}%, manufacturing/cutting waste only)`, value: wasteSum, unit: 'kg' },
        { label: 'Procurement Weight', value: procurementSum, unit: 'kg' },
      ]
      combined.quantityBases = {
        theoretical: { label: 'Theoretical Steel Weight', valueKg: theoreticalSum, valueTon: theoreticalSum / 1000 },
        theoreticalPlusLap: { label: 'Theoretical + Lap', valueKg: theoreticalPlusLapSum, valueTon: theoreticalPlusLapSum / 1000 },
        procurement: { label: 'Procurement Weight', valueKg: procurementSum, valueTon: procurementSum / 1000 },
      }
      return { value: combined, error: null }
    } catch (e) {
      return { value: null, error: e.message }
    }
  }, [beamsForCalc, longitudinalForCalc, stirrupsForCalc, lapPct, wastePct])

  async function handleSave() {
    if (!result.value) return
    const chosen = result.value.quantityBases[selectedBasis]

    const geometrySnapshot = {
      items: beamsForCalc.map((b) => ({
        takeoff_item_id: b.id, measurement_type: 'length', value: b.length, unit: 'm', label: b.label,
        detail: { width: b.width, overallDepth: b.overallDepth },
      })),
    }
    const engineeringInputs = { beamDims, covers, longitudinalGroups, stirrupZones, lapPct: Number(lapPct), wastePct: Number(wastePct), quantityBasisChosen: selectedBasis }

    const succeeded = await onSave({
      calculatedValueTon: chosen.valueTon,
      unit: 'ton',
      geometrySnapshot,
      engineeringInputs,
      breakdownSnapshot: { steps: result.value.steps, beamBreakdown: result.value.beamBreakdown, quantityBases: result.value.quantityBases },
      takeoffItemIdForProvenance: [...selectedBeamIds][0] || null,
    })
    if (succeeded !== false) clearDraft()
  }

  const hasUnresolvedDims = beamsForCalc.some((b) => Number.isNaN(b.width) || Number.isNaN(b.overallDepth))

  return (
    <div>

      <div style={{ padding: '8px 10px', background: '#FFF8E1', border: `1px solid #F5C542`, borderRadius: 6, fontSize: 11, color: '#7A5C00', marginBottom: 14 }}>
        ⚠ Stirrup zones must cover the full beam length with no gaps or overlaps — this is validated strictly. Longitudinal bar counts are always explicit estimator inputs, never derived from spacing.
      </div>

      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>Select Beam(s)</label>
      <div style={{ border: `1px solid ${colors.border}`, borderRadius: 6, maxHeight: 120, overflowY: 'auto', marginBottom: 10 }}>
        {linkedItems.filter((it) => it.measurement_type === 'length').length === 0 && <div style={{ padding: 10, fontSize: 12, color: colors.textMuted }}>No length geometry linked to this scope yet.</div>}
        {linkedItems.filter((it) => it.measurement_type === 'length').map((item) => {
          const resolved = resolvedBeams[item.id]
          return (
            <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: `1px solid ${colors.border}`, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={selectedBeamIds.has(item.id)} onChange={() => toggleBeam(item.id)} />
              <span style={{ flex: 1 }}>{item.description}</span>
              {selectedBeamIds.has(item.id) && resolved && <span style={{ color: colors.textMuted, fontSize: 11 }}>{formatNumber(resolved.length)} m</span>}
            </label>
          )
        })}
      </div>

      {/* Per-beam width/depth (real section dimensions, not derivable from plan geometry) */}
      {beamsForCalc.map((b) => (
        <div key={b.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: colors.textSecondary }}>{b.label} ({formatNumber(b.length)}m)</span>
          <LabeledInput label="Width (m)" type="number" value={beamDims[b.id]?.width ?? ''} onChange={(e) => updateBeamDim(b.id, { width: e.target.value })} />
          <LabeledInput label="Overall Depth (m)" type="number" value={beamDims[b.id]?.overallDepth ?? ''} onChange={(e) => updateBeamDim(b.id, { overallDepth: e.target.value })} />
        </div>
      ))}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Top Cover (mm)</label>
          <Input type="number" value={covers.top} onChange={(e) => setCovers((p) => ({ ...p, top: e.target.value }))} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Bottom Cover (mm)</label>
          <Input type="number" value={covers.bottom} onChange={(e) => setCovers((p) => ({ ...p, bottom: e.target.value }))} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Side Cover (mm)</label>
          <Input type="number" value={covers.side} onChange={(e) => setCovers((p) => ({ ...p, side: e.target.value }))} style={{ width: '100%' }} />
        </div>
      </div>

      {/* Longitudinal Groups */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Longitudinal Reinforcement</label>
        <Button size="sm" variant="secondary" onClick={addLongitudinalGroup}>+ Add Group</Button>
      </div>
      {longitudinalGroups.map((g) => (
        <div key={g.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Group name (e.g. Bottom Main)" value={g.label} onChange={(e) => updateLongitudinalGroup(g.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeLongitudinalGroup(g.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 6, marginBottom: 6 }}>
            <Select value={g.position} onChange={(e) => updateLongitudinalGroup(g.id, { position: e.target.value })} style={{ fontSize: 11 }}>
              <option value="top">Top</option><option value="bottom">Bottom</option><option value="side">Side</option>
            </Select>
            <Select value={g.diameter} onChange={(e) => updateLongitudinalGroup(g.id, { diameter: e.target.value })} style={{ fontSize: 11 }}>
              <option value="" disabled>Ø (mm)</option>
              {BAR_DIAMETERS.map((d) => <option key={d} value={d}>Ø{d}</option>)}
            </Select>
            <LabeledInput label="No. of Bars" type="number" value={g.numberOfBars} onChange={(e) => updateLongitudinalGroup(g.id, { numberOfBars: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            <div>
              <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>Hook Allowance (mm, optional)</label>
              <Input type="number" value={g.hookAllowance} onChange={(e) => updateLongitudinalGroup(g.id, { hookAllowance: e.target.value })} style={{ fontSize: 11, width: '100%' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>Development Length Override (mm, optional)</label>
              <Input type="number" value={g.developmentLengthOverride} onChange={(e) => updateLongitudinalGroup(g.id, { developmentLengthOverride: e.target.value })} style={{ fontSize: 11, width: '100%' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>Explicit Bar Length (m, optional)</label>
              <Input type="number" value={g.explicitBarLength} onChange={(e) => updateLongitudinalGroup(g.id, { explicitBarLength: e.target.value })} style={{ fontSize: 11, width: '100%' }} />
            </div>
          </div>
        </div>
      ))}

      {/* Stirrup Zones */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '14px 0 8px' }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Stirrup Zones</label>
        <Button size="sm" variant="secondary" onClick={addStirrupZone}>+ Add Zone</Button>
      </div>
      {stirrupZones.map((z, zIndex) => {
        const isLastZone = zIndex === stirrupZones.length - 1
        const firstBeamLength = beamsForCalc[0]?.length
        return (
        <div key={z.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Input placeholder="Zone name (e.g. Support Zone A)" value={z.label} onChange={(e) => updateStirrupZone(z.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
            <button onClick={() => removeStirrupZone(z.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ marginBottom: 6 }}>
            <LabeledInput label="Start Offset (m)" type="number" value={z.startOffset} onChange={(e) => updateStirrupZone(z.id, { startOffset: e.target.value })} />
          </div>

          {/* End Offset gets its own full-width row — combined with the
              "Use Beam Length" toggle, it needs more room than a
              cramped grid column can give it. */}
          <div style={{ marginBottom: 6 }}>
            <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>End Offset (m)</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Input
                type="number" value={z.useBeamLengthAsEnd ? '' : z.endOffset}
                placeholder={z.useBeamLengthAsEnd ? `${firstBeamLength != null ? firstBeamLength.toFixed(2) : ''} m (beam length)` : ''}
                disabled={z.useBeamLengthAsEnd}
                onChange={(e) => updateStirrupZone(z.id, { endOffset: e.target.value })}
                style={{ fontSize: 11, flex: 1, minWidth: 0 }}
              />
              {isLastZone && (
                <button
                  onClick={() => updateStirrupZone(z.id, { useBeamLengthAsEnd: !z.useBeamLengthAsEnd, endOffset: '' })}
                  title="Set End Offset to the exact beam length automatically"
                  style={{
                    fontSize: 10, padding: '6px 10px', whiteSpace: 'nowrap', borderRadius: 4, cursor: 'pointer', flexShrink: 0,
                    border: `1px solid ${z.useBeamLengthAsEnd ? colors.primary : colors.border}`,
                    background: z.useBeamLengthAsEnd ? colors.primary : '#fff',
                    color: z.useBeamLengthAsEnd ? '#fff' : colors.textSecondary,
                    fontWeight: z.useBeamLengthAsEnd ? 700 : 400,
                  }}
                >
                  {z.useBeamLengthAsEnd ? '✓ Using Beam Length' : 'Use Beam Length'}
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(85px, 1fr))', gap: 6, marginBottom: 6 }}>
            <div>
              <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>Diameter</label>
              <Select value={z.diameter} onChange={(e) => updateStirrupZone(z.id, { diameter: e.target.value })} style={{ fontSize: 11, width: '100%' }}>
                <option value="" disabled>Ø (mm)</option>
                {BAR_DIAMETERS.map((d) => <option key={d} value={d}>Ø{d}</option>)}
              </Select>
            </div>
            <LabeledInput label="Spacing (mm)" type="number" value={z.spacing} onChange={(e) => updateStirrupZone(z.id, { spacing: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            <div>
              <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>Legs</label>
              <Select value={z.legs} onChange={(e) => updateStirrupZone(z.id, { legs: e.target.value })} style={{ fontSize: 11, width: '100%' }}>
                <option value={2}>2-Leg</option><option value={4}>4-Leg</option>
              </Select>
            </div>
            <LabeledInput label="Hooks per Stirrup (count)" type="number" value={z.numberOfHooksPerStirrup} onChange={(e) => updateStirrupZone(z.id, { numberOfHooksPerStirrup: e.target.value })} />
            <LabeledInput label="Hook Length per Hook (mm)" type="number" value={z.hookLengthPerHook} onChange={(e) => updateStirrupZone(z.id, { hookLengthPerHook: e.target.value })} />
          </div>
        </div>
      )})}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10, marginBottom: 14 }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Lap/Splice % — required</label>
          <Input type="number" value={lapPct} onChange={(e) => setLapPct(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Waste % — required</label>
          <Input type="number" value={wastePct} onChange={(e) => setWastePct(e.target.value)} style={{ width: '100%' }} />
        </div>
      </div>

      {result.value && (
        <div style={{ marginBottom: 14 }}>
          {result.value.beamBreakdown.map((bb, bi) => (
            <div key={bi} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 8, background: '#FAFBFC' }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{bb.beam.label} — {formatNumber(bb.beam.length)}m, {formatNumber(bb.beam.width)}×{formatNumber(bb.beam.overallDepth)}m</div>
              {bb.longitudinalResults.map((lr, li) => (
                <div key={'l' + li} style={{ fontSize: 11, color: colors.textSecondary, padding: '3px 0' }}>
                  <strong>{lr.group.label || 'Group'}</strong> ({lr.group.position}, Ø{lr.group.diameter}, ×{lr.group.numberOfBars}): Length {formatNumber(lr.barLength)}m → <strong>{formatNumber(lr.grossWeight)} kg</strong>
                </div>
              ))}
              {bb.stirrupResults.map((sr, si) => (
                <div key={'s' + si} style={{ fontSize: 11, color: colors.textSecondary, padding: '3px 0' }}>
                  <strong>{sr.zone.label || 'Zone'}</strong> (Ø{sr.zone.diameter}@{sr.zone.spacing}mm, {sr.zone.legs}-leg): {sr.numberOfStirrups} stirrups, actual spacing {formatNumber(sr.actualSpacing * 1000)}mm, cutting {formatNumber(sr.cuttingLength)}m → <strong>{formatNumber(sr.grossWeight)} kg</strong>
                </div>
              ))}
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${colors.border}`, fontSize: 12, fontWeight: 700 }}>Beam Total: {formatNumber(bb.beamGross)} kg</div>
            </div>
          ))}
          <div style={{ padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8 }}>
            {result.value.steps.map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
                <span style={{ color: colors.textSecondary }}>{s.label}</span><span style={{ fontWeight: 600 }}>{formatNumber(s.value)} {s.unit}</span>
              </div>
            ))}
          </div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginTop: 12, marginBottom: 5 }}>Save as Resource Quantity Output — choose basis</label>
          <Select value={selectedBasis} onChange={(e) => setSelectedBasis(e.target.value)} style={{ width: '100%' }}>
            <option value="theoretical">{result.value.quantityBases.theoretical.label} — {formatNumber(result.value.quantityBases.theoretical.valueTon)} ton</option>
            <option value="theoreticalPlusLap">{result.value.quantityBases.theoreticalPlusLap.label} — {formatNumber(result.value.quantityBases.theoreticalPlusLap.valueTon)} ton</option>
            <option value="procurement">{result.value.quantityBases.procurement.label} — {formatNumber(result.value.quantityBases.procurement.valueTon)} ton</option>
          </Select>
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 14, fontStyle: 'italic' }}>{result.error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!superseding && (
          <button
            onClick={() => {
              if (window.confirm('Discard all unsaved input for this module? This cannot be undone.')) {
                clearDraft()
                onClose()
              }
            }}
            style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}
          >
            Discard Draft
          </button>
        )}
        <Button onClick={handleSave} disabled={!result.value || saving || hasUnresolvedDims} style={{ marginLeft: 'auto' }}>
          {saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}
        </Button>
      </div>
    </div>
  )
}

function LabeledInput({ label, ...inputProps }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label>
      <Input {...inputProps} style={{ fontSize: 11, width: '100%' }} />
    </div>
  )
}
